import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import {
  ChangeRequestStatus,
  CONFIG_TYPES,
  ConfigType,
  ConfigurationManagementService,
  EntityType,
} from '../services/configurationManagement.service';
import { useFeatureFlags, useFeatureFlagsQuery } from './featureFlags';

const QUERY_KEY_BASE = 'changeRequests';

/**
 * Hook to fetch a single change request by ID
 */
export const useChangeRequestById = (id: number) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'byId', id],
    queryFn: () => ConfigurationManagementService.getById(id),
    enabled: ccr_enabled === true && !!id && hasPermission,
  });
};

/**
 * Hook to check if self-review is allowed for a change request.
 * Returns true if the current user can approve their own request.
 */
export const useSelfReviewPermission = (id: number | undefined) => {
  const hasPermission = useHasPermission('configurations:write:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'selfReviewPermission', id],
    queryFn: () => ConfigurationManagementService.getSelfReviewPermission(id as number),
    enabled: ccr_enabled === true && !!id && hasPermission,
    select: (data) => data.allowed,
  });
};

export interface UseChangeRequestsOptions {
  enabled?: boolean;
  status?: ChangeRequestStatus;
}

/**
 * Hook to fetch change requests for a specific entity.
 * @param entityType - The type of entity (e.g. 'fleet', 'authority')
 * @param entityId - The ID of the entity
 * @param options - Options object with enabled and status filter
 */
export const useChangeRequests = (entityType: EntityType, entityId: string, options?: UseChangeRequestsOptions) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  const { enabled = true, status } = options ?? {};

  const queryKey = [QUERY_KEY_BASE, entityType, entityId, status];

  return useQuery({
    queryKey,
    queryFn: () => ConfigurationManagementService.listChangeRequests(entityType, entityId, status),
    enabled: ccr_enabled === true && !!entityId && enabled && hasPermission,
  });
};

export interface UsePendingChangeRequestsOptions {
  enabled?: boolean;
  configType?: ConfigType;
}

/**
 * Hook to fetch pending change requests for a specific entity.
 * @param entityType - The type of entity (e.g. 'fleet', 'authority')
 * @param entityId - The ID of the entity
 * @param options - Options object with enabled and configType filter
 * @returns Query result with pendingChangeRequests array and hasPendingChangeRequest boolean
 */
export const usePendingChangeRequests = (entityType: EntityType, entityId: string, options?: UsePendingChangeRequestsOptions) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const featureFlagsQuery = useFeatureFlagsQuery();
  const ccr_enabled = featureFlagsQuery.data?.ccr_enabled;

  const { enabled = true, configType } = options ?? {};
  const queryEnabled = ccr_enabled === true && !!entityId && enabled && hasPermission;

  const queryKey = [QUERY_KEY_BASE, entityType, entityId, ChangeRequestStatus.Pending];

  const query = useQuery({
    queryKey,
    queryFn: () => ConfigurationManagementService.listChangeRequests(entityType, entityId, ChangeRequestStatus.Pending),
    enabled: queryEnabled,
  });

  const pendingChangeRequests = useMemo(() => {
    if (!ccr_enabled || !Array.isArray(query.data)) {
      return [];
    }

    return query.data.filter((cr) => cr.status === ChangeRequestStatus.Pending && (!configType || cr.config_type === configType));
  }, [query.data, configType, ccr_enabled]);

  return {
    ...query,
    pendingChangeRequests,
    hasPendingChangeRequest: pendingChangeRequests.length > 0,
    isCheckingPendingChangeRequest: featureFlagsQuery.isInitialLoading || (queryEnabled && query.isInitialLoading),
  };
};

type UseFleetChangeRequestOptions = Omit<UsePendingChangeRequestsOptions, 'configType'>;

const useFleetPendingChangeRequest = (fleetUuid: string | undefined, configType: ConfigType, options?: UseFleetChangeRequestOptions) => {
  const query = usePendingChangeRequests('fleet', fleetUuid ?? '', {
    ...options,
    configType,
    enabled: !!fleetUuid && (options?.enabled ?? true),
  });

  return {
    ...query,
    pendingChangeRequest: query.pendingChangeRequests[0],
  };
};

export const useFleetConfigChangeRequest = (fleetUuid?: string, options?: UseFleetChangeRequestOptions) =>
  useFleetPendingChangeRequest(fleetUuid, CONFIG_TYPES.FLEET_CONFIG, options);

export const useFleetMetadataChangeRequest = (fleetUuid?: string, options?: UseFleetChangeRequestOptions) =>
  useFleetPendingChangeRequest(fleetUuid, CONFIG_TYPES.FLEET_METADATA, options);

export const useFleetEventTypesChangeRequest = (fleetUuid?: string, options?: UseFleetChangeRequestOptions) =>
  useFleetPendingChangeRequest(fleetUuid, CONFIG_TYPES.FLEET_EVENT_TYPES, options);

export const useFleetEnforcementExceptionsChangeRequest = (fleetUuid?: string, options?: UseFleetChangeRequestOptions) =>
  useFleetPendingChangeRequest(fleetUuid, CONFIG_TYPES.FLEET_ENFORCEMENT_EXCEPTIONS, options);

export const useFleetPermittedPlatesChangeRequest = (fleetUuid?: string, options?: UseFleetChangeRequestOptions) =>
  useFleetPendingChangeRequest(fleetUuid, CONFIG_TYPES.FLEET_PERMITTED_PLATES, options);

export const useFleetBlockMappingChangeRequest = (fleetUuid?: string, options?: UseFleetChangeRequestOptions) =>
  useFleetPendingChangeRequest(fleetUuid, CONFIG_TYPES.FLEET_BLOCK_MAPPING, options);

export const useFleetOTAConfigChangeRequest = (fleetUuid?: string, options?: UseFleetChangeRequestOptions) =>
  useFleetPendingChangeRequest(fleetUuid, CONFIG_TYPES.FLEET_OTA_CONFIG, options);

type UseAuthorityChangeRequestOptions = Omit<UsePendingChangeRequestsOptions, 'configType'>;

const useAuthorityPendingChangeRequest = (
  authorityUuid: string | undefined,
  configType: ConfigType,
  options?: UseAuthorityChangeRequestOptions,
) => {
  const query = usePendingChangeRequests('authority', authorityUuid ?? '', {
    ...options,
    configType,
    enabled: !!authorityUuid && (options?.enabled ?? true),
  });

  return {
    ...query,
    pendingChangeRequest: query.pendingChangeRequests[0],
  };
};

export const useAuthorityConfigChangeRequest = (authorityUuid?: string, options?: UseAuthorityChangeRequestOptions) =>
  useAuthorityPendingChangeRequest(authorityUuid, CONFIG_TYPES.AUTHORITY_CONFIG, options);

export const useAuthorityMetadataChangeRequest = (authorityUuid?: string, options?: UseAuthorityChangeRequestOptions) =>
  useAuthorityPendingChangeRequest(authorityUuid, CONFIG_TYPES.AUTHORITY_METADATA, options);

/**
 * Hook to fetch change requests for the current fleet (from useFleet context).
 */
export const useCurrentFleetChangeRequests = (options?: UseChangeRequestsOptions) => {
  const fleet = useFleet();

  return useChangeRequests('fleet', fleet.uuid, options);
};

/**
 * Hook to fetch change requests for multiple authority entity IDs.
 * Runs one query per authority and merges the results.
 */
export const useChangeRequestsForAuthorities = (authorityUuids: string[], options?: UseChangeRequestsOptions) => {
  const hasPermission = useHasPermission('configurations:read:change_requests');
  const { ccr_enabled } = useFeatureFlags();

  const { enabled = true, status } = options ?? {};

  const queries = useQueries({
    queries: authorityUuids.map((authorityUuid) => ({
      queryKey: [QUERY_KEY_BASE, 'authority', authorityUuid, status],
      queryFn: () => ConfigurationManagementService.listChangeRequests('authority', authorityUuid, status),
      enabled: ccr_enabled === true && !!authorityUuid && enabled && hasPermission,
    })),
  });

  const data = useMemo(() => {
    const allResults = queries.flatMap((q) => q.data ?? []);

    return allResults;
  }, [queries]);

  return {
    data,
    isLoading: queries.some((q) => q.isLoading),
  };
};

export const forceRefreshChangeRequests = () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
};
